import type { ModeInfo } from '../../../shared/modes';
import { QUICK_MESSAGES } from '../../../shared/modes';
import type { ServerMsg } from '../../../shared/protocol';
import type { GameClient } from '../game/GameClient';
import { formatDate, t, type Key } from '../i18n';
import { Icon } from './icons';

const clock = (s: number) => `${Math.floor(s / 60)}:${String(Math.max(0, s) % 60).padStart(2, '0')}`;
import { colors } from '../game/colors';

const teamColor = (i: number) => colors().teams[i] ?? '#8d99a6';

/** Scores, minuteur, objectif, vagues ; bandeau « aucune récompense » en partie personnalisée. */
export function ModeHud({ info, myTeam }: { info: ModeInfo; myTeam: number | null }) {
  if (info.kind === 'ffa') return null;
  return (
    <div className="mode-hud" data-testid="mode-hud">
      {info.noRewards && <div className="banner banner-bonus">{t('noRewards')}</div>}
      <div className="mode-line">
        <strong>{t(`mode_${info.kind}` as Key)}</strong>
        {info.phase === 'waiting' && <span>{t(info.noRewards ? 'matchStartsIn' : 'waitingPlayers', { s: info.waitS ?? 0 })}</span>}
        {info.phase === 'running' && info.kind !== 'survival' && <span>{clock(info.timeLeft)}</span>}
        {info.phase === 'overtime' && <span className="danger">{t('overtime', { t: clock(info.timeLeft) })}</span>}
      </div>
      {info.scores && (
        <div className="scores">
          {info.scores.map((s, i) => (
            <span key={i} style={{ color: teamColor(i) }}>
              {i === myTeam ? '★ ' : ''}
              {s}
              {info.target ? ` / ${info.target}` : ''}
            </span>
          ))}
        </div>
      )}
      {info.points && (
        <div className="dom-points">
          {info.points.map((p, i) => (
            <span key={i} className="dom-point" style={{ borderColor: p.owner >= 0 ? teamColor(p.owner) : '#8d99a6' }}>
              {'ABC'[i]}
              {p.progress > 0 && p.team >= 0 && <small style={{ color: teamColor(p.team) }}> {Math.round(p.progress * 100)} %</small>}
            </span>
          ))}
        </div>
      )}
      {info.kind === 'survival' && (
        <div className="mode-line">
          <span>{t('waveOf', { n: info.wave ?? 0, max: info.waves ?? 30 })}</span>
          <span>{t(`diff_${info.difficulty}` as Key)}</span>
          {info.phase === 'prep' && <span>{t('prepIn', { s: info.timeLeft })}</span>}
        </div>
      )}
      {typeof info.bots === 'number' && info.bots > 0 && <div className="wip">{t('botsInMatch', { n: info.bots })}</div>}
    </div>
  );
}

export function MatchResult({ info, myTeam, onLeave }: { info: ModeInfo; myTeam: number | null; onLeave: () => void }) {
  if (info.phase !== 'ended') return null;
  const won = info.kind === 'survival' ? info.winner === 0 : info.winner !== null && info.winner === myTeam;
  const draw = info.kind !== 'survival' && info.winner === null;
  return (
    <div className="death" data-testid="match-result">
      <div className={`death-card result-card ${draw ? 'draw' : won ? 'won' : 'lost'}`}>
        <div className="hazard" aria-hidden />
        <h2 className="display result-title">{draw ? t('draw') : won ? t('victory') : t('defeat')}</h2>
        {info.scores && (
          <p className="result-scores display num">
            {info.scores.map((sc, i) => (
              <span key={i} style={{ color: teamColor(i) }}>
                {i === myTeam ? '★ ' : ''}
                {sc}
              </span>
            ))}
          </p>
        )}
        {info.kind === 'survival' && <p>{t('waveOf', { n: info.wave ?? 0, max: info.waves ?? 30 })}</p>}
        {!info.noRewards && <p className="wip">{t('teamRewardInfo')}</p>}
        <button type="button" className="btn btn-primary" onClick={onLeave}>
          <Icon name="exit" size={16} />
          {t('back')}
        </button>
      </div>
    </div>
  );
}

/** Contrôles de l'hôte d'un salon privé : verrouillage, expulsion, transfert, fermeture propre. */
export function LobbyPanel({ lobby, client }: { lobby: Extract<ServerMsg, { t: 'lobby' }>; client: GameClient }) {
  return (
    <div className="lobby-panel">
      <div className="lb-title">
        {t('lobbyCode')} <code className="lobby-code">{lobby.code}</code> {lobby.locked && (
          <span role="img" aria-label={t('lock')}>
            <Icon name="lock" size={14} />
          </span>
        )}
      </div>
      <div className="wip">{t('lobbyExpires', { d: formatDate(lobby.expiresAt, 'time') })}</div>
      <ul>
        {lobby.players.map((p) => (
          <li key={p.id}>
            {p.bot && <em className="badge-bot">{t('bot')}</em>}
            {p.name}
            {lobby.host && !p.bot && (
              <>
                <button className="chip" onClick={() => client.lobby('host', p.id)}>
                  {t('makeHost')}
                </button>
                <button className="chip" onClick={() => client.lobby('kick', p.id)}>
                  {t('kick')}
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
      {lobby.host && (
        <div className="hud-row">
          <button className="chip" onClick={() => client.lobby(lobby.locked ? 'unlock' : 'lock')}>
            {lobby.locked ? t('unlock') : t('lock')}
          </button>
          <button className="chip" onClick={() => client.lobby('close')}>
            {t('closeLobby')}
          </button>
        </div>
      )}
    </div>
  );
}

export function QuickBar({ client }: { client: GameClient }) {
  return (
    <div className="quick-bar" aria-label={t('quickTitle')}>
      {QUICK_MESSAGES.map((k, i) => (
        <button key={k} type="button" className="chip" onClick={() => client.quick(i)} title={`F${i + 1}`}>
          <kbd className="key key-xs">F{i + 1}</kbd>
          {t(`quick_${k}` as Key)}
        </button>
      ))}
    </div>
  );
}
